import React from 'react';
import glamorous from 'glamorous';
import {WHITE, CHARCOAL, LIGHT_SHADED_GREY} from '../../../shared/style/colors';
import {
  PHONE,
  TABLET,
  PHONE_LANDSCAPE,
  DESKTOP,
  MEDIUM,
  LAPTOP
} from '../../../shared/style/breakpoints';
import {BASE_TEXT, FONTS, WEIGHT} from '../../../shared/style/typography';
import SimpleButton from '../../../shared/library/buttons/base/simple';
import {ALPHA} from '../../../shared/style/color-utils';
import {MAX_PAGE_WIDTH} from '../../../shared/style/dimensions';

const Container = glamorous.div(({background = WHITE}) => ({
  display: 'block',
  marginBottom: 120,
  background: background,
  padding: '0 115px',
  [DESKTOP]: {
    padding: 0,
    maxWidth: MAX_PAGE_WIDTH,
    margin: 'auto',
    marginBottom: 120
  },
  [PHONE]: {
    padding: '0 20px',
    marginBottom: 90
  }
}));

const Title = glamorous.h3({
  ...BASE_TEXT,
  fontFamily: FONTS.CEREBRI_SANS_PRO,
  fontStyle: 'normal',
  fontWeight: WEIGHT.ULTRA_BLACK,
  fontSize: 40,
  lineHeight: '50px',
  textAlign: 'center',
  color: CHARCOAL,
  margin: '0 0 10px 0',
  [PHONE]: {
    fontSize: 28,
    lineHeight: '39px',
    margin: 'auto',
    marginBottom: 12
  }
});

const SubTitle = glamorous.h4({
  ...BASE_TEXT,
  fontFamily: FONTS.CEREBRI_SANS_PRO,
  fontStyle: 'normal',
  fontWeight: WEIGHT.NORMAL,
  fontSize: 24,
  lineHeight: '44px',
  textAlign: 'center',
  color: CHARCOAL,
  margin: '0 0 20px 0',
  [PHONE]: {
    fontSize: 18,
    lineHeight: '29px',
    margin: 'auto'
  }
});

const ContentOne = glamorous.div({
  textAlign: 'center',
  [DESKTOP]: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gridColumnGap: 20,
    marginTop: 35
  },
  '> img': {
    marginTop: -19,
    position: 'absolute',
    right: '50%'
  }
});

const ContentTwo = glamorous.div({
  textAlign: 'center',
  [DESKTOP]: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gridColumnGap: 20,
    margin: '-12px 10px 0 90px',
    paddingLeft: 110
  },
  [MEDIUM]: {
    marginTop: -24
  },
  [LAPTOP]: {
    marginTop: 0
  },
  '> img': {
    marginTop: -19,
    position: 'absolute',
    right: '50%'
  }
});

const ContentCard = glamorous.div(
  {
    boxShadow: '0px 5px 45px rgba(0, 0, 0, 0.1)',
    borderRadius: 8,
    background: WHITE,
    border: `1px solid ${LIGHT_SHADED_GREY}`,
    display: 'inline-flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    padding: '45px 15px 0 40px',
    marginTop: 20,
    '> img': {
      width: 100,
      height: 80,
      marginBottom: 30,
      [PHONE]: {
        width: 120,
        marginLeft: -10,
        marginBottom: 20
      }
    },
    [DESKTOP]: {
      position: 'relative',
      paddingLeft: 20,
      height: 360
    },
    [PHONE]: {
      height: 336,
      paddingLeft: 30,
      marginTop: 20
    },
    [TABLET]: {
      height: 340,
      marginTop: 20
    },
    [MEDIUM]: {
      marginTop: 20
    }
  },
  ({customStyle}) => ({
    [DESKTOP]: {
      marginTop: customStyle && 0
    }
  })
);

const ContentTitle = glamorous.h4({
  ...BASE_TEXT,
  fontFamily: FONTS.CEREBRI_SANS_PRO,
  width: 238,
  margin: '16px 0',
  fontStyle: 'normal',
  fontSize: 20,
  fontWeight: WEIGHT.NORMAL,
  textAlign: 'left',
  color: CHARCOAL,
  lineHeight: '40px',
  marginTop: 15,
  marginRight: 7,
  [PHONE]: {
    fontSize: 24
  }
});

const ExploreEnterprise = glamorous(SimpleButton)({
  minHeight: 58,
  background: CHARCOAL,
  border: 'none',
  borderRadius: 4,
  fontSize: 19,
  fontWeight: WEIGHT.ULTRA_BOLD,
  letterSpacing: 'normal',
  [DESKTOP]: {
    width: 373,
    height: 58
  },
  [PHONE_LANDSCAPE]: {
    marginLeft: 0,
    marginTop: 20
  },
  [PHONE]: {
    fontSize: 18,
    fontWeight: WEIGHT.BOLD
  },
  '&:hover': {
    backgroundColor: ALPHA(CHARCOAL, 0.9),
    border: `1px solid ${CHARCOAL}`
  },
  ':focus': {
    color: WHITE
  }
}).withComponent('a');

const ContentArrowContainer = glamorous.div({
  display: 'flex',
  width: 330,
  [PHONE]: {
    width: 280,
    height: 88,
    display: 'block'
  },
  '> img': {
    position: 'absolute',
    right: -13,
    marginLeft: 32
  }
});

const ArrowContainer = glamorous.div({
  textAlign: 'center',
  marginLeft: 35,
  paddingLeft: 35,
  marginTop: -10,
  height: 70,
  '> img': {
    position: 'relative',
    marginLeft: 35,
    paddingLeft: 86,
    paddingBottom: 10,
    zIndex: 1
  },
  [LAPTOP]: {
    display: 'none'
  }
});

const ButtonContainer = glamorous.div({
  display: 'flex',
  justifyContent: 'center',
  marginTop: 60
});

const ArrowIcon = glamorous.img({
  [LAPTOP]: {
    display: 'none'
  }
});

const ArrowDownIcon = glamorous.img({
  [DESKTOP]: {
    display: 'none'
  }
});

const ArrowFlowIcon = glamorous.img({
  [LAPTOP]: {
    display: 'none'
  }
});

const options = [
  {
    id: 1,
    iconLink: 'https://img.stackshare.io/fe/landing/connect.svg',
    title: (
      <>
        <strong>1. Connect</strong> all your Git repos in a few clicks.
      </>
    )
  },
  {
    id: 2,
    iconLink: 'https://img.stackshare.io/fe/landing/configure.svg',
    title: (
      <>
        <strong>2. Configure</strong> approved technologies, applications, add team members.
      </>
    )
  },
  {
    id: 3,
    iconLink: 'https://img.stackshare.io/fe/landing/explore.svg',
    title: (
      <>
        <strong>3. Explore</strong> automatically generated stack profiles and data for every Git
        repo.
      </>
    )
  },
  {
    id: 4,
    iconLink: 'https://img.stackshare.io/fe/landing/auto-update.svg',
    title: (
      <>
        <strong> 4. Auto-update</strong> your stack profiles with every commit to a default branch.
      </>
    )
  },
  {
    id: 5,
    iconLink: 'https://img.stackshare.io/fe/landing/track.svg',
    title: (
      <>
        <strong>5. Track</strong> tech stack changes in real time via email or Slack &amp; MS Teams.
      </>
    )
  }
];

const arrowConnectIcon = 'https://img.stackshare.io/fe/landing/arrow_connect.svg';
const arrowIcon = 'https://img.stackshare.io/fe/landing/arrow.svg';
const arrowDownIcon = 'https://img.stackshare.io/fe/landing/arrow_down.svg';

const StackIntelligenceWorkflow = () => {
  return (
    <Container>
      <Title>Your stack intelligence workflow</Title>
      <SubTitle>Install and get up and running in less than 5 minutes</SubTitle>
      <ContentOne>
        {options.slice(0, 3).map(item => (
          <>
            <ContentCard key={item.id}>
              <img src={item.iconLink} />
              <ContentArrowContainer>
                <ContentTitle>{item.title}</ContentTitle>
                {item.id < 3 && <ArrowIcon src={arrowIcon} />}
              </ContentArrowContainer>
            </ContentCard>
            {item.id > 1 && <ArrowDownIcon src={arrowDownIcon} />}
          </>
        ))}
      </ContentOne>

      <ArrowContainer>
        <ArrowFlowIcon src={arrowConnectIcon} />
      </ArrowContainer>

      <ContentTwo>
        {options.slice(3).map(item => (
          <>
            <ContentCard key={item.id} customStyle>
              <img src={item.iconLink} />
              <ContentArrowContainer id={item.id}>
                <ContentTitle>{item.title}</ContentTitle>
                {item.id === 4 && <ArrowIcon src={arrowIcon} />}
              </ContentArrowContainer>
            </ContentCard>
            <ArrowDownIcon src={arrowDownIcon} />
          </>
        ))}
      </ContentTwo>
      <ButtonContainer>
        <ExploreEnterprise href={`/enterprise`}>Explore StackShare Enterprise</ExploreEnterprise>
      </ButtonContainer>
    </Container>
  );
};

export default StackIntelligenceWorkflow;
